import { Box, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import { messages as globalMessages } from '@databases/intl/messages';
import { ViewId } from '@databases/types/database/view';
import { type DatabaseView } from '@databases/types/database/view';
import React, {
  type FunctionComponent,
  useMemo,
  useEffect,
  useState,
  useRef,
} from 'react';
import { useIntl } from 'react-intl-next';

export interface ViewNameWithTruncationProps {
  view: DatabaseView;
  isDragging: boolean;
}

const containerStyles = xcss({
  maxWidth: '175px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const ViewNameWithTruncation: FunctionComponent<ViewNameWithTruncationProps> = ({
  view,
  isDragging,
}) => {
  const viewNameRef = useRef<HTMLDivElement | null>(null);
  const [isViewNameTruncated, setIsViewNameTruncated] = useState(false);

  useEffect(() => {
    if (viewNameRef.current) {
      const { scrollWidth, clientWidth } = viewNameRef.current;
      setIsViewNameTruncated(scrollWidth > clientWidth);
    }
  }, [view.name]);

  const { formatMessage } = useIntl();

  const translatedViewName = useMemo(() => {
    if (view.id === ViewId.UNSAVED) {
      return formatMessage(globalMessages['view.unsaved-view']);
    }

    return view.name;
  }, [formatMessage, view.id, view.name]);

  const tooltipContent = useMemo(() => {
    return isViewNameTruncated && !isDragging ? translatedViewName : '';
  }, [isViewNameTruncated, isDragging, translatedViewName]);

  return isViewNameTruncated && !isDragging ? (
    <Tooltip content={tooltipContent} position="bottom" testId="view-name-tooltip">
      <Box xcss={containerStyles} ref={viewNameRef} testId="view-name-tab">
        {translatedViewName}
      </Box>
    </Tooltip>
  ) : (
    <Box xcss={containerStyles} ref={viewNameRef} testId="view-name-tab">
      {translatedViewName}
    </Box>
  );
};

export default ViewNameWithTruncation;
